import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { UserPlus, Loader2, ShieldCheck, Users } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InviteMemberModal({ open, onOpenChange }: Props) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("OPERADOR");
  const [sending, setSending] = useState(false);
  const { orgId } = useAuth();

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const handleSend = async () => {
    if (!isValidEmail || !orgId) return;
    setSending(true);

    try {
      const { data, error } = await supabase.functions.invoke("trigger-n8n-invite", {
        body: { email: email.trim(), rol: role, org_id: orgId },
      });

      if (error) throw error;

      toast.success(`Invitación enviada a ${email.trim()}`);
      setEmail("");
      setRole("OPERADOR");
      onOpenChange(false);
    } catch (err: any) {
      const msg = err?.message || err?.context?.error || "Error al enviar la invitación.";
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-primary" />
            Invitar Miembro
          </DialogTitle>
          <DialogDescription>
            Envía una invitación por correo electrónico para unirse a tu organización.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="invite-email">Correo electrónico</Label>
            <Input
              id="invite-email"
              type="email"
              placeholder="nombre@empresa.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
            />
          </div>

          {/* Role */}
          <div className="space-y-3">
            <Label>Rol</Label>
            <RadioGroup value={role} onValueChange={setRole} className="gap-3">
              <label
                htmlFor="role-admin"
                className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                  role === "ADMIN"
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-muted-foreground/30"
                }`}
              >
                <RadioGroupItem value="ADMIN" id="role-admin" className="mt-0.5" />
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5 font-medium text-sm text-foreground">
                    <ShieldCheck className="h-3.5 w-3.5 text-primary" />
                    Admin
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Puede invitar miembros, gestionar configuraciones y ver facturación.
                  </p>
                </div>
              </label>

              <label
                htmlFor="role-operador"
                className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                  role === "OPERADOR"
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-muted-foreground/30"
                }`}
              >
                <RadioGroupItem value="OPERADOR" id="role-operador" className="mt-0.5" />
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5 font-medium text-sm text-foreground">
                    <Users className="h-3.5 w-3.5 text-muted-foreground" />
                    Miembro
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Puede procesar documentos y operar embarques. Sin acceso a configuraciones administrativas.
                  </p>
                </div>
              </label>
            </RadioGroup>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>
            Cancelar
          </Button>
          <Button onClick={handleSend} disabled={!isValidEmail || sending} className="gap-2">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
            Enviar Invitación
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
